import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';

import { gtag } from "../..";
import { useEffect } from "react";

const WhatIsPlanningPoker = () => {
    useEffect(() => {
        gtag('event', 'page_view', {
            page_title: 'What is Planning Poker',
            page_path: '/what-is-planning-poker'
        });
    });

    return (
        <div className="about">
            <Typography component="h1" variant="h1">
                What is Planning Poker? A Complete Guide for Agile Teams
            </Typography>
            <Card>
                <CardContent className='summary-content'>
                    <Typography component="p" variant="p">
                        Planning Poker, also known as Scrum Poker, is a consensus-based estimation technique used by agile software development teams. It was first described by James Grenning in 2002 and later popularized by Mike Cohn in his book <em>Agile Estimating and Planning</em>.
                    </Typography>

                    <Typography component="h2" variant="h2">
                        Why Teams Use Planning Poker
                    </Typography>
                    <Typography component="p" variant="p">
                        Traditional estimation methods often suffer from anchoring bias — when one person states an estimate first, others tend to adjust their own estimates toward that number. Planning Poker solves this by having everyone reveal their estimates simultaneously, ensuring each team member thinks independently before seeing others' opinions.
                    </Typography>
                    <Typography component="p" variant="p">
                        Research has shown that group estimation techniques like Planning Poker produce more accurate estimates than individual expert estimation. A 2006 study by Moloekken-Oestvold and Haugen found that group estimates were more accurate than the average individual estimate in 74% of cases.
                    </Typography>

                    <Typography component="h2" variant="h2">
                        How a Planning Poker Session Works
                    </Typography>
                    <Typography component="p" variant="p">
                        A typical Planning Poker session follows these steps:
                    </Typography>
                    <ol>
                        <li><Typography component="span" variant="body1">A moderator (usually the Product Owner or Scrum Master) presents a user story or task to the team.</Typography></li>
                        <li><Typography component="span" variant="body1">The team discusses the story briefly to clarify requirements, ask questions, and identify potential challenges.</Typography></li>
                        <li><Typography component="span" variant="body1">Each team member privately selects a card representing their estimate of the effort required.</Typography></li>
                        <li><Typography component="span" variant="body1">All cards are revealed simultaneously.</Typography></li>
                        <li><Typography component="span" variant="body1">If estimates differ significantly, the team members with the highest and lowest estimates explain their reasoning.</Typography></li>
                        <li><Typography component="span" variant="body1">The team discusses and re-estimates until consensus is reached.</Typography></li>
                    </ol>

                    <Typography component="h2" variant="h2">
                        Story Points vs. Time Estimates
                    </Typography>
                    <Typography component="p" variant="p">
                        Planning Poker typically uses story points rather than hours or days. Story points represent relative effort — a story estimated at 5 points should require roughly 2.5 times the effort of a 2-point story. This abstraction is intentional: it shifts the focus from "how long will this take?" to "how complex is this relative to other work we've done?"
                    </Typography>
                    <Typography component="p" variant="p">
                        Teams often use a reference story as a baseline. For example, a well-understood task that the team agrees is a "3" becomes the yardstick against which other stories are measured.
                    </Typography>

                    <Typography component="h2" variant="h2">
                        Common Card Sequences
                    </Typography>
                    <Typography component="p" variant="p">
                        The most popular sequence is the modified Fibonacci series: 0, 1, 2, 3, 5, 8, 13, 21. Some teams also include special cards like "?" (unsure/need more information) and a coffee cup (need a break). The gaps between larger numbers are intentional — they reflect the increasing uncertainty in estimating larger pieces of work. Learn more about <a href="/fibonacci-estimation">why Fibonacci numbers work for estimation</a>.
                    </Typography>

                    <Typography component="h2" variant="h2">
                        When to Use Planning Poker
                    </Typography>
                    <Typography component="p" variant="p">
                        Planning Poker is most effective during sprint planning and backlog refinement sessions. It works best when the team has 3 to 10 members. For larger groups, consider breaking into smaller estimation teams and comparing results.
                    </Typography>
                    <Typography component="p" variant="p">
                        It's less useful for tasks that are already well-understood by the entire team or for purely operational work with predictable effort.
                    </Typography>

                    <Typography component="h2" variant="h2">
                        Tips for Effective Sessions
                    </Typography>
                    <ul>
                        <li><Typography component="span" variant="body1"><strong>Time-box discussions:</strong> Limit discussion to 2-3 minutes per story before voting. Extended debates often signal that the story needs to be broken down further.</Typography></li>
                        <li><Typography component="span" variant="body1"><strong>Don't force consensus:</strong> If after two rounds the team can't agree, go with the higher estimate. The goal is a shared understanding, not a precise number.</Typography></li>
                        <li><Typography component="span" variant="body1"><strong>Include everyone:</strong> QA, designers, and DevOps engineers often catch complexity that developers miss. Every perspective improves the estimate.</Typography></li>
                        <li><Typography component="span" variant="body1"><strong>Track accuracy:</strong> Compare estimates to actual effort over time. This helps the team calibrate and improve.</Typography></li>
                    </ul>
                    <Typography component="p" variant="p">
                        Ready to try it? <a href="/">Create a free Scrum Poker room</a> and invite your team to start estimating together.
                    </Typography>
                </CardContent>
            </Card>
        </div>
    );
}

export default WhatIsPlanningPoker;
